/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import type { ModalDialog, Service, ServiceGroup } from '../../../../../type';

import { GET_SERVICE_GROUPS_QUERY } from '../ServicesList';

type DeleteServiceDialogProps = {
    service: Service,
    serviceGroup: ServiceGroup,
} & ModalDialog;

// Mutation that deletes specified service
const DELETE_SERVICE_MUTATION = gql`
    mutation deleteService($input: DeleteServiceInput!) {
        deleteService(input: $input) {
            serviceId
        }
    }
`;

const DeleteServiceDialog = ({
    service,
    serviceGroup,
    trigger,
    open,
    onClose,
}: DeleteServiceDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const [deleteService] = useMutation(DELETE_SERVICE_MUTATION);

    const onDelete = () => {
        const variables = { salon, lang: currentLanguage };
        return deleteService({
            variables: {
                input: { serviceId: service.id },
            },
            // Optimistic response removes the service from the UI before the
            // server responds.
            optimisticResponse: {
                __typename: 'Mutation',
                deleteService: {
                    __typename: 'DeleteServicePayload',
                    serviceId: service.id,
                },
            },
            // Remove the deleted service from the cache (immutable rewrite —
            // readQuery results are frozen in AC3).
            update: (store, { data: { deleteService: result } }) => {
                try {
                    const data = store.readQuery({
                        query: GET_SERVICE_GROUPS_QUERY,
                        variables,
                    });
                    if (!data) return;
                    store.writeQuery({
                        query: GET_SERVICE_GROUPS_QUERY,
                        variables,
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    serviceGroups: {
                                        ...data.viewer.salon.serviceGroups,
                                        nodes: data.viewer.salon.serviceGroups.nodes.map(
                                            sg =>
                                                sg.id === serviceGroup.id
                                                    ? {
                                                          ...sg,
                                                          services: sg.services.filter(
                                                              s =>
                                                                  s.id !==
                                                                  result.serviceId,
                                                          ),
                                                      }
                                                    : sg,
                                        ),
                                    },
                                },
                            },
                        },
                    });
                } catch (e) {
                    // It's safe to ignore this exception
                }
            },
        }).then(() => onClose());
    };

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
        <Header icon>
            <Icon name="remove" />
            <FormattedMessage
                id="pages.settings.services.delete.deleteHeader"
                defaultMessage="Delete Service"
            />
        </Header>
        <Modal.Content>
            <FormattedMessage
                id="pages.settings.services.delete.deleteText"
                defaultMessage="Are you sure you want to delete {serviceName} service ?"
                values={{
                    serviceName: service.name,
                }}
            />
        </Modal.Content>
        <Modal.Actions>
            <Button onClick={onClose} color="red" basic inverted>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.settings.services.delete.no"
                    defaultMessage="No"
                />
            </Button>
            <Button onClick={onDelete} color="green" inverted>
                <Icon name="checkmark" />
                <FormattedMessage
                    id="pages.settings.services.delete.yes"
                    defaultMessage="Yes"
                />
            </Button>
        </Modal.Actions>
        </Modal>
    );
};

export default DeleteServiceDialog;
